/**
 * Platform analytics – send events to backend ingest (company_site).
 * All tracking is fire-and-forget: no await, never blocks UI or navigation.
 * Uses REACT_APP_API_URL if set; otherwise in dev uses http://localhost:3000/v1 so tracking works without config.
 *
 * GTM / Google Ads / GA4 events are NOT pushed from this module — they're handled
 * by src/utils/track.js, which is wired in App.js via a global click delegate
 * (catches every CTA targeting portal.stylon.co/onboarding) and route-change
 * listener (catches every page view). Keeping the two pipelines separate avoids
 * double-firing and keeps backend ingest concerns separate from ad tech.
 */

const STORAGE_KEY = 'stylon_company_site_visitor';
const SOURCE = 'company_site';

// Per-tab "first-touch" attribution context. We capture UTM params,
// the referrer and the landing path on the very first event of a
// session (or whenever URL params reveal a fresh campaign hit) and
// stamp every subsequent event with that context — otherwise once the
// user clicks into a sub-page the URL no longer carries the UTMs and
// we'd lose attribution for everything past the landing.
const ATTRIBUTION_STORAGE_KEY = 'stylon_company_site_attribution';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

function readSessionStorage(key) {
  try { return sessionStorage.getItem(key); } catch (_) { return null; }
}
function writeSessionStorage(key, value) {
  try { sessionStorage.setItem(key, value); } catch (_) {}
}

function getStoredGclidLocal() {
  // Mirror of track.js's reader — kept local so this module has no
  // dependency on the ad-tech layer (they can be deleted independently).
  try {
    const ts = parseInt(localStorage.getItem('stylon_gclid_ts') || '0', 10);
    if (!ts) return '';
    const ageDays = (Date.now() - ts) / 86_400_000;
    if (ageDays > 90) return '';
    return localStorage.getItem('stylon_gclid') || '';
  } catch (_) { return ''; }
}

function getStoredFbclidLocal() {
  // Mirror of track.js's reader — kept local so this module has no
  // dependency on the ad-tech layer (they can be deleted independently).
  try {
    const ts = parseInt(localStorage.getItem('stylon_fbclid_ts') || '0', 10);
    if (!ts) return '';
    const ageDays = (Date.now() - ts) / 86_400_000;
    if (ageDays > 90) return '';
    return localStorage.getItem('stylon_fbclid') || '';
  } catch (_) { return ''; }
}

function captureAttributionFromUrl() {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const hasAnyUtm = UTM_KEYS.some((k) => params.get(k));
    const existing = readSessionStorage(ATTRIBUTION_STORAGE_KEY);
    // If we already captured for this tab AND the current URL doesn't
    // carry a new campaign, keep the first-touch.
    if (existing && !hasAnyUtm) return JSON.parse(existing);

    const attribution = {
      // Initial referrer — Google/Facebook/direct/organic etc. Empty
      // string when the user typed the URL or came from a same-origin
      // navigation (treat that as "direct" downstream).
      referrer: (typeof document !== 'undefined' && document.referrer) || '',
      landingPath: window.location.pathname || '/',
      landingQuery: window.location.search || '',
    };
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) attribution[k] = v;
    });
    writeSessionStorage(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
    return attribution;
  } catch (_) {
    return null;
  }
}

function getAttribution() {
  // Lazy: capture on first call, reuse thereafter.
  const cached = readSessionStorage(ATTRIBUTION_STORAGE_KEY);
  if (cached) {
    try { return JSON.parse(cached); } catch (_) {}
  }
  return captureAttributionFromUrl();
}

function getVisitorKey() {
  try {
    let key = localStorage.getItem(STORAGE_KEY);
    if (!key) {
      key = 'cs_' + Math.random().toString(36).slice(2) + '_' + Date.now();
      localStorage.setItem(STORAGE_KEY, key);
    }
    return key;
  } catch (_) {
    return null;
  }
}

function getApiBase() {
  const env = process.env.REACT_APP_API_URL || '';
  if (env.trim()) {
    const base = env.replace(/\/+$/, '');
    // Ensure the URL ends with /v1 — guard against env var missing the path segment
    return base.endsWith('/v1') ? base : base + '/v1';
  }
  if (typeof window !== 'undefined' && window.location) {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return 'http://localhost:3000/v1';
    return window.location.origin + '/v1';
  }
  return 'http://localhost:3000/v1';
}

/**
 * Map the current pathname to a coarse "page" key for backend grouping.
 * Examples:
 *   "/" → "home"
 *   "/for/barber-shops" → "for/barber-shops"
 *   "/features/online-booking" → "features/online-booking"
 *   "/pricing" → "pricing"
 * Kept narrow (slugs only, no query) so the dashboard's "Group by page"
 * facet doesn't explode into thousands of distinct values.
 */
function currentPageKey() {
  if (typeof window === 'undefined') return undefined;
  const path = window.location.pathname || '/';
  if (path === '/' || path === '') return 'home';
  // Strip leading slash and trailing slashes.
  return path.replace(/^\/+/, '').replace(/\/+$/, '');
}

/**
 * If the current path is /for/<vertical>, return the vertical slug.
 * Lets the admin dashboard answer "which business-type ads converted"
 * without re-parsing the URL on the server.
 */
function currentVertical() {
  if (typeof window === 'undefined') return undefined;
  const m = (window.location.pathname || '').match(/^\/for\/([^/?#]+)/);
  return m ? m[1] : undefined;
}

export function trackCompanySiteEvent(eventType, payload = {}) {
  const base = getApiBase();
  if (!base) return;

  // Stamp every event with attribution (UTMs + referrer + landing
  // path), the current page and — for /for/<vertical> — the vertical
  // slug. The backend stores these in the jsonb `payload`, so existing
  // ingest validation (it accepts arbitrary fields) keeps working.
  const attribution = getAttribution() || {};
  const enrichedPayload = {
    ...attribution,
    page: currentPageKey(),
    vertical: currentVertical(),
    gclid: getStoredGclidLocal() || undefined,
    fbclid: getStoredFbclidLocal() || undefined,
    ...payload, // explicit call-site payload wins so per-event fields override context
  };

  // Drop empty / undefined keys so the stored jsonb stays compact.
  Object.keys(enrichedPayload).forEach((k) => {
    if (enrichedPayload[k] === undefined || enrichedPayload[k] === '' || enrichedPayload[k] === null) {
      delete enrichedPayload[k];
    }
  });

  const body = {
    source: SOURCE,
    eventType,
    visitorKey: getVisitorKey() || undefined,
    payload: Object.keys(enrichedPayload).length ? enrichedPayload : undefined,
  };

  try {
    fetch(`${base}/analytics/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch (_) {}
}

/** Page view (e.g. on home load) with locale */
export function trackPageView(locale) {
  trackCompanySiteEvent('page_view', { locale: locale || undefined });
}

/** Try now button – source: hero | navbar | navbar_mobile | about | book_appointment | use_stylon | faq | contact | cta_block */
export function trackTryNowClick(source) {
  trackCompanySiteEvent('try_now_click', { source: source || 'cta_block' });
}

/** Contact Us link/button click */
export function trackContactClick() {
  trackCompanySiteEvent('contact_click');
}

/** WhatsApp click – section: 'navbar' | 'footer' | 'contact' */
export function trackWhatsAppClick(section = 'navbar') {
  trackCompanySiteEvent('whatsapp_click', { section });
}

/** "Book Demo" button — goes to stylon.online (customer-facing demo salon). */
export function trackBookDemoClick(source = 'cta_block') {
  trackCompanySiteEvent('book_demo_click', { source });
}

/** Scroll depth percent (25, 50, 75, 100). Call when user crosses threshold. */
export function trackScroll(percent) {
  trackCompanySiteEvent('scroll', { percent });
}

/** FAQ question opened */
export function trackQaOpen(questionId) {
  trackCompanySiteEvent('qa_open', { questionId });
}

const MAX_STRING = { message: 2000, stack: 5000, componentStack: 5000, url: 500 };
function truncate(str, max) {
  if (typeof str !== 'string') return str;
  return str.length <= max ? str : str.slice(0, max) + '…';
}

/**
 * Drop errors we know are not actionable so the production log stays
 * useful. Mirrors the web-store filter (see
 * stylon-web-store-page-master/src/utils/errorReporting.js).
 * Categories filtered:
 *  - Browser-extension stacks (chrome-extension:// etc.) — user-
 *    installed extensions mutating the DOM, not our code.
 *  - Facebook in-app-browser native-bridge races (`iabjs://` schemes
 *    + the "Java object is gone" postMessage error on FB Android).
 *  - Cross-origin "Script error." with no useful stack.
 *  - Third-party CDN sniffs we can't fix: the Enable.co.il
 *    accessibility widget probes window.safari.pushNotification and
 *    crashes on browsers that lack it; window.webkit.messageHandlers
 *    is similarly probed by injected scripts. The HTML inline guards
 *    at public/index.html already neutralise these for our own code;
 *    third-party-script races still occasionally slip through and
 *    those reports are noise.
 */
function isNoise(error, context) {
  const stack = String(error?.stack || '');
  const message = String(error?.message || error?.toString?.() || '');

  // Extension frames are usually wrapped in parens, e.g.
  // `at document.createEvent (chrome-extension://…)`, so match anywhere — the
  // old line-anchored pattern missed those and let "Illegal invocation" through.
  if (/(?:chrome|moz|safari|edge|opera)-extension:\/\//i.test(stack)) return true;
  if (/iabjs:\/\//.test(stack)) return true;
  if (/\bsendDataToNative\b|\bsendPageHideMessage\b/.test(stack)) return true;
  if (message === 'Error invoking postMessage: Java object is gone') return true;
  if (message === 'Script error.' && !stack) return true;
  if (/e\.safari\.pushNotification|window\.safari\.pushNotification/.test(message)) return true;
  if (/window\.webkit\.messageHandlers/.test(message)) return true;

  return false;
}

/** Report a client error to the backend (fire-and-forget). Shown in Admin → Analytics → Errors. */
export function reportClientError(error, context = {}) {
  if (isNoise(error, context)) return;
  const message = error?.message || error?.toString?.() || String(error);
  const payload = {
    type: context.type || 'error',
    message: truncate(message, MAX_STRING.message),
    url: typeof window !== 'undefined' && window.location ? window.location.href : undefined,
    stack: error?.stack ? truncate(error.stack, MAX_STRING.stack) : undefined,
    componentStack: context.componentStack ? truncate(context.componentStack, MAX_STRING.componentStack) : undefined,
  };
  if (context.userAgent !== false && typeof navigator !== 'undefined' && navigator.userAgent) {
    payload.userAgent = truncate(navigator.userAgent, 400);
  }
  trackCompanySiteEvent('client_error', payload);
}

/**
 * Get first-touch attribution data (referrer and landing path).
 * Used by URL builders (e.g., for portal deep links) to pass attribution context.
 */
export function getFirstTouch() {
  try {
    const raw = readSessionStorage(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return {};
    const a = JSON.parse(raw);
    return { referrer: a.referrer || undefined, landingPath: a.landingPath || undefined };
  } catch (_) { return {}; }
}

/**
 * Get the company site visitor key.
 * Used by URL builders to pass visitor context to the portal.
 */
export function getCompanySiteVisitorKey() { return getVisitorKey(); }
